import type { DonutSlice, LegendItem } from 'features/common/components/Punchup/primitives/charts/PunchupDonutChart';
import { toUsFormat } from 'utils/utils';
import { formatCompactCurrency } from 'features/common/utils/chartFormatters';
import { hexToRgb, rgbToHex, lightenHex } from 'features/common/utils/chartColors';
import { common } from 'theme';
import { TicketType } from 'features/common/types';

// `lightenHex` now lives in the shared chart-color module (also used by the Show-Day attendance donut);
// imported above for this module's own use and re-exported so the inventory builders/tests keep their
// `./inventoryUtils` import.
export { lightenHex };

export const COLOR_AVAIL = common.colors.grey19;
/** Center-label highlight for an over-subscribed pool (issued > shared capacity). */
export const COLOR_OVERSOLD = common.colors.red;
/**
 * Neutral mid-grey for the "Pool full" slice: a pooled tier's own leftover seats that the SHARED pool
 * can no longer sell. Distinct from the light `COLOR_AVAIL` (open inventory) — it reads as "present but
 * not sellable", not as available. Deliberately NOT a member colour (the old brown pool wedge, bug 2c).
 * `grey9` (#828282) sits just BELOW the [135,180] luminance band `expiredShiftHex` produces, so a pooled
 * tier that is also expired keeps its "Pool full" and "Expired" slices visually distinct.
 */
export const COLOR_POOL_BLOCKED = common.colors.grey9;
export const LIGHTEN_FACTOR = 0.7;

/**
 * Paid-ticket count = issued minus comped. The single source of the "comps
 * capture no revenue" rule shared by every inventory revenue computation (tile,
 * summary roll-up, and both fill-ring builders). Clamped at 0 to guard against
 * any upstream comped > issued inconsistency.
 */
export const paidCount = (issued: number, comped: number): number => Math.max(0, issued - comped);

// ---------------------------------------------------------------------------
// Pool derivations — Lifetime-Σ (docs/pools_business_logic.md §3)
// ---------------------------------------------------------------------------
//
// Per-pool usage (sold = Σ member issued, poolRemaining, sellableRemaining, slack) and per-type poolBlocked
// are derived ONCE in `useEventCapacity` and read straight off the `CapacityPool` / `CapacityType` — no
// builder recomputes them, so every pool surface agrees by construction.

/**
 * The most a shared pool can EARN IN TOTAL — the "revenue ceiling" shown on the pool card's third row.
 * It is the revenue already received plus the most still achievable from the pool's REMAINING shared
 * capacity: the highest-priced member tiers sell first, each up to its pool-capped `available`, until the
 * shared remaining is exhausted (greedy):
 *   sort priced members by unit price desc; additional += price × min(poolRemaining, tier available); stop at 0.
 * So a fully-sold pool (poolRemaining 0) has a ceiling equal to what it already received → the fill ring
 * reads 100% (nothing more to earn), matching the pool-capped per-type rings (docs/pools_business_logic.md §3).
 * Free tiers (price 0) and tiers with 0 pool-capped availability contribute nothing. Unit price is derived
 * per tier as `ceiling / trueCapacity` (= initialPrice for priced, maxPrice for PWYW).
 */
export function computePoolRevenueCeiling(
	received: number,
	members: { available: number; ceiling: number; trueCapacity: number; initialPrice: number }[],
	poolRemaining: number
): number {
	const priced = members
		.map((m) => ({
			price: m.trueCapacity > 0 ? m.ceiling / m.trueCapacity : m.initialPrice,
			available: m.available,
		}))
		.filter((m) => m.price > 0 && m.available > 0)
		.sort((a, b) => b.price - a.price);

	let remaining = poolRemaining;
	let additional = 0;
	for (const m of priced) {
		if (remaining <= 0) break;
		const take = Math.min(remaining, m.available);
		additional += m.price * take;
		remaining -= take;
	}
	return received + additional;
}

export type PriceTier = 'priced' | 'pwyw' | 'free';

export const TIER_ORDER: Record<PriceTier, number> = { priced: 0, pwyw: 1, free: 2 };

export function getPriceTier(tt: Pick<TicketType, 'payWhatYouWant' | 'initialPrice'>): PriceTier {
	if (tt.payWhatYouWant) return 'pwyw';
	if (tt.initialPrice === 0) return 'free';
	return 'priced';
}

/**
 * Single source of truth for inventory column ordering — shared by the data
 * adapter (`CapacityType`) and any `TicketType`-shaped caller:
 *   priced → unit price descending (highest first)
 *   pwyw   → name descending (Z before A)
 *   free   → name descending (Z before A)
 *
 * Generic over the minimal shape both row types expose, so the rule lives once.
 * Callers spread before sorting (`[...rows].sort(compareByTier)`) — the
 * comparator never mutates.
 */
export function compareByTier<T extends Pick<TicketType, 'payWhatYouWant' | 'initialPrice' | 'name'>>(
	a: T,
	b: T
): number {
	const tierA = getPriceTier(a);
	const tierB = getPriceTier(b);
	const tierDiff = TIER_ORDER[tierA] - TIER_ORDER[tierB];
	if (tierDiff !== 0) return tierDiff;
	if (tierA === 'priced') return b.initialPrice - a.initialPrice;
	return b.name.localeCompare(a.name);
}

export function formatPriceLabel(tt: Pick<TicketType, 'payWhatYouWant' | 'initialPrice'>): string {
	if (tt.payWhatYouWant) return 'Pay what you want';
	if (tt.initialPrice === 0) return 'Free';
	return toUsFormat(tt.initialPrice);
}

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

/**
 * Convert a hex color to a luminance-based pure grey (R=G=B) in the
 * mid-grey band [135, 180] out of 255.
 *
 * Used for expired-capacity segments so they read as neutral / "past" without
 * competing with the colored available (lightened type color) or sold segments.
 * Different type colors produce different grey shades so multi-type expired
 * segments remain visually distinguishable in the summary donut.
 *
 * Aesthetic rationale:
 *   - Pure greyscale (no warm tint) contrasts cleanly with the lightened type
 *     colors used for available segments.
 *   - Mid-grey band (not too dark, not too light) prevents expired segments
 *     from visually dominating the ring or blending into the background.
 *   - Luminance-based mapping (ITU-R BT.709) preserves perceived brightness
 *     relationships between types.
 */
// Mid-grey band the expired shift maps into: [EXPIRED_GREY_MIN, EXPIRED_GREY_MIN + EXPIRED_GREY_RANGE]
// out of 255 — dark enough not to wash out, light enough not to dominate the ring.
const EXPIRED_GREY_MIN = 135;
const EXPIRED_GREY_RANGE = 45;

export function expiredShiftHex(hex: string): string {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const [r, g, b] = rgb;
	const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
	const grey = Math.round(EXPIRED_GREY_MIN + (luma / 255) * EXPIRED_GREY_RANGE);
	return rgbToHex(grey, grey, grey);
}

// ---------------------------------------------------------------------------
// Revenue formatting
// ---------------------------------------------------------------------------

/**
 * Compact currency format for fill ring center labels.
 * Re-exported from the shared `chartFormatters` module (single source of truth
 * for compact number/currency formatting across analytics charts and tiles).
 */
export const formatCompactRevenue = formatCompactCurrency;

/**
 * recharts donut `tooltipFormatter` for a revenue slice: full-precision currency + the slice name.
 * Shared by the summary and per-type fill rings so both read identically.
 */
export const revenueTooltipFormatter = (value: number, name: string): [string, string] => [toUsFormat(value), name];

/**
 * Center label for a per-type revenue fill ring.
 * Shows total revenue collected for this type (compact format).
 * Returns "—" when there is no capacity (avoids division by zero).
 */
export function formatFillCenterLabel(col: { payout: number; trueCapacity: number }): string {
	if (col.trueCapacity === 0) return '—';
	return formatCompactRevenue(col.payout);
}

/**
 * Center label for the summary revenue fill ring.
 * Shows total revenue across all types (compact format).
 */
export function formatSummaryFillCenterLabel(totalPayout: number, totalCapacity: number): string {
	if (totalCapacity === 0) return '—';
	return formatCompactRevenue(totalPayout);
}

// ---------------------------------------------------------------------------
// Fill-ring legend ordering — single source so the summary and per-type fill
// legends can never drift. "Available" sits immediately after "Sold" (the two
// primary capacity states), then the secondary states. Callers tag the segments
// they actually have with a key; this orders them and strips the key.
// ---------------------------------------------------------------------------

export type FillSegment = 'sold' | 'available' | 'poolFull' | 'comped' | 'onHold' | 'expired';

const FILL_LEGEND_ORDER: FillSegment[] = ['sold', 'available', 'poolFull', 'comped', 'onHold', 'expired'];

export interface KeyedLegendItem extends LegendItem {
	key: FillSegment;
}

export function orderFillLegend(items: KeyedLegendItem[]): LegendItem[] {
	return (
		[...items]
			.sort((a, b) => FILL_LEGEND_ORDER.indexOf(a.key) - FILL_LEGEND_ORDER.indexOf(b.key))
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			.map(({ key, ...rest }) => rest)
	);
}

// ---------------------------------------------------------------------------
// Fill ring data builders
// ---------------------------------------------------------------------------

/**
 * Builds the fill-ring data array for PWYW/free types and pool earning rings (simple payout/ceiling model).
 * - ceiling=0, payout=0 → full grey ring (no revenue ceiling defined — the lone `No ceiling` placeholder)
 * - payout > 0, ceiling=0 → full fillColor ring labelled "Overflow" (PWYW/free paid above with no ceiling)
 * - payout >= ceiling     → full fillColor ring labelled "Received" (ceiling fully captured — 100%, NOT overflow)
 * - payout <= 0          → full grey ring carrying the FULL ceiling as "Remaining" (nothing received yet)
 * - otherwise             → [payout segment, remaining grey segment]
 *
 * Every drawable slice carries a REAL dollar value (the payout, the remaining, or the ceiling) — never a `1`
 * sentinel — so a revenue `tooltipFormatter` shows the actual figure (e.g. "$0 received of $500") instead of
 * "$1.00". Only the genuine no-ceiling placeholder has no dollar meaning; callers format it without a `$`.
 */
export function buildFillRingData(payout: number, ceiling: number, fillColor: string): DonutSlice[] {
	if (ceiling <= 0 && payout <= 0) {
		return [{ label: 'No ceiling', value: 1, color: COLOR_AVAIL }];
	}
	if (ceiling <= 0) {
		// No fixed ceiling but revenue arrived (PWYW/free paid above floor) — genuine overflow.
		return [{ label: 'Overflow', value: payout, color: fillColor }];
	}
	if (payout >= ceiling) {
		// Ceiling fully captured (e.g. a sold-out pool: received == ceiling) — 100%, not overflow.
		return [{ label: 'Received', value: payout, color: fillColor }];
	}
	if (payout <= 0) {
		// Nothing received yet but there IS a ceiling — the full grey ring carries the ceiling so a hover reads
		// the real remaining potential ("$500 · Remaining"), not the old "$1.00" sentinel.
		return [{ label: 'Remaining', value: ceiling, color: COLOR_AVAIL }];
	}
	return [
		{ label: 'Payout', value: payout, color: fillColor },
		{ label: 'Remaining', value: ceiling - payout, color: COLOR_AVAIL },
	];
}

/** Minimal column shape needed to build a segmented fill ring (subset of InventoryColumn). */
interface PricedColumnInput {
	issued: number;
	compedCount: number; // comped tickets (issued at $0 — never captured revenue)
	hold: { amount: number } | null; // per-type reserve only (not a pool)
	available: number; // pool-capped sellable remaining (revenue still achievable)
	poolBlocked?: number; // pooled tier's own leftover seats the shared pool can't sell (0 / absent = standalone)
	expired: number;
	initialPrice: number;
}

/* Segment labels below ('Sold' / 'Comped' / 'On Hold' / 'Available' / 'Expired'
 * / 'No ceiling' / 'Overflow' / 'Remaining') are fixed English presentation
 * defaults used as recharts slice/tooltip text. They are intentionally NOT
 * threaded through i18n here — keeping this a pure, `t`-free formatting util.
 * Casing matches the `analytics-inventory-*` i18n values the builders use for
 * the visible legends, so a tier card never shows the same term in two casings. */

/**
 * Builds a revenue fill ring for a priced ticket type.
 * Segments mirror the issued donut but scaled by unit price:
 *   Sold      = PAID × price          (typeColor — captured revenue)
 *   Comped    = comped × price        (compedColor — given away, $0 captured)
 *   On Hold   = reserve × price       (holdColor — only when a reserve exists)
 *   Available = available × price     (lightened typeColor — revenue still achievable)
 *   Pool full = poolBlocked × price   (neutral grey — own seats the shared pool can't sell)
 *   Expired   = expired × price       (warm-grey shift of typeColor)
 *
 * Comped seats are issued but capture no revenue, so they form their own slice
 * (not part of "Sold") — the captured-revenue arc is paid-only, matching the
 * tile/center value. `available` is the POOL-CAPPED sellable remaining, so for a
 * pooled tier whose pool is full it is 0 and no revenue is shown as still
 * achievable; the pool-blocked seats render as a neutral "Pool full" slice (their
 * revenue can't be earned). The ring still sums to trueCapacity × price. For
 * standalone tiers poolBlocked is 0 (unchanged). Only call for priced types
 * (initialPrice > 0, !payWhatYouWant).
 */
export function buildSegmentedFillRingData(
	col: PricedColumnInput,
	typeColor: string,
	holdColor: string,
	compedColor: string
): DonutSlice[] {
	const price = col.initialPrice;
	const paid = paidCount(col.issued, col.compedCount);
	const soldRev = paid * price;
	const compedRev = col.compedCount * price;
	const holdRev = (col.hold?.amount ?? 0) * price;
	const availRev = col.available * price;
	const blockedRev = (col.poolBlocked ?? 0) * price;
	const expiredRev = col.expired * price;

	const availColor = lightenHex(typeColor, LIGHTEN_FACTOR);
	const expiredColor = expiredShiftHex(typeColor);

	const slices: DonutSlice[] = [
		soldRev > 0 ? { label: 'Sold', value: soldRev, color: typeColor } : null,
		compedRev > 0 ? { label: 'Comped', value: compedRev, color: compedColor } : null,
		holdRev > 0 ? { label: 'On Hold', value: holdRev, color: holdColor } : null,
		availRev > 0 ? { label: 'Available', value: availRev, color: availColor } : null,
		blockedRev > 0 ? { label: 'Pool full', value: blockedRev, color: COLOR_POOL_BLOCKED } : null,
		expiredRev > 0 ? { label: 'Expired', value: expiredRev, color: expiredColor } : null,
	].filter(Boolean) as DonutSlice[];

	if (slices.length === 0) return [{ label: 'Remaining', value: 1, color: COLOR_AVAIL }];
	return slices;
}

export function emptyRing(): DonutSlice[] {
	return [{ label: 'Available', value: 1, color: COLOR_AVAIL }];
}
